import { Injectable } from '@angular/core';

export interface PdfComplaint {
  complaintNo?: string;
  complainantTitle?: string;
  complainantName?: string;
  gender?: string;
  cnic?: string;
  cellPhoneNo?: string;
  email?: string;
  passportNo?: string;
  address?: string;
  segmentId?: number;
  priority?: string;
  severity?: string;
  complaintCategoryName?: string;
  complaintSubcategoryName?: string;
  legalFramework?: string;
  daysRequired?: string;
  expectedResolutionDate?: string;
  divisionName?: string;
  districtName?: string;
  tehsilName?: string;
  mouzaName?: string;
  complaintAddress?: string;
  otherForum?: string;
  assignToName?: string;
  assignToGroupName?: string;
  complaintDetails?: string;
  workCode?: string;
  helplineIdentifier?: string;
  complaintSource?: string;
  currentProcess?: { name?: string };
}

export interface PdfTrackingItem {
  createdByName?: string;
  createdAt?: string;
  comment?: string;
  statusChangedName?: string;
  priorityChanged?: string;
  severityChanged?: string;
  documentName?: string;
  isRemove?: boolean | null;
  complaintEmailMessage?: string;
  complaintSMSMessage?: string;
  assignedToName?: string;
  assignedToGroupName?: string;
}

@Injectable({ providedIn: 'root' })
export class ComplaintPdfService {

  generate(complaint: PdfComplaint | undefined, trackingList: PdfTrackingItem[], canSeeInfo: boolean): void {
    const c = complaint;
    const s = (v: any): string => (v != null && v !== '') ? String(v) : '—';
    const fd = (v: any): string => v ? this.formatDate(v, 'dd MMM yyyy') : '—';
    const fdt = (v: any): string => v ? this.formatDate(v, 'dd MMM yyyy hh:mm a') : '—';
    const segment = c?.segmentId === 1 ? 'PULSE' : c?.segmentId === 2 ? 'PLRA' : '—';

    const field = (label: string, value: string, urdu = ''): string =>
      `<tr>
        <td class="lbl">${label}${urdu ? `<span class="ul"> / ${urdu}</span>` : ''}</td>
        <td class="val">${value}</td>
      </tr>`;

    const citizenSection = canSeeInfo ? `
      <div class="section-title">Citizen Details <span class="ul">/ شہری کی تفصیلات</span></div>
      <table>
        ${field('Name', s((c?.complainantTitle || '') + ' ' + (c?.complainantName || '')).trim(), 'نام')}
        ${field('Gender', s(c?.gender), 'جنس')}
        ${field('CNIC', s(c?.cnic), 'شناختی کارڈ')}
        ${field('Phone', s(c?.cellPhoneNo), 'فون')}
        ${field('Email', s(c?.email), 'ای میل')}
        ${field('Passport No', s(c?.passportNo), 'پاسپورٹ نمبر')}
        ${field('Address', s(c?.address), 'پتہ')}
      </table>` : '';

    const activityRows = (trackingList || []).map(t => {
      const rows: string[] = [];
      if (t.statusChangedName) rows.push(`<div class="act-row"><span class="act-lbl">Status</span><span class="act-val">${s(t.statusChangedName)}</span></div>`);
      if (t.comment) rows.push(`<div class="act-row"><span class="act-lbl">Comment</span><span class="act-val">${s(t.comment)}</span></div>`);
      if (t.assignedToName) rows.push(`<div class="act-row"><span class="act-lbl">Assigned To</span><span class="act-val">${s(t.assignedToName)}</span></div>`);
      if (t.assignedToGroupName) rows.push(`<div class="act-row"><span class="act-lbl">Group</span><span class="act-val">${s(t.assignedToGroupName)}</span></div>`);
      if (t.priorityChanged) rows.push(`<div class="act-row"><span class="act-lbl">Priority</span><span class="act-val">${s(t.priorityChanged)}</span></div>`);
      if (t.severityChanged) rows.push(`<div class="act-row"><span class="act-lbl">Severity</span><span class="act-val">${s(t.severityChanged)}</span></div>`);
      if (t.documentName && t.isRemove === null) rows.push(`<div class="act-row"><span class="act-lbl">Attachment Added</span><span class="act-val">${s(t.documentName)}</span></div>`);
      if (t.documentName && t.isRemove === true) rows.push(`<div class="act-row"><span class="act-lbl">Attachment Removed</span><span class="act-val">${s(t.documentName)}</span></div>`);
      if (t.complaintEmailMessage) rows.push(`<div class="act-row"><span class="act-lbl">Email</span><span class="act-val">${s(t.complaintEmailMessage)}</span></div>`);
      if (t.complaintSMSMessage) rows.push(`<div class="act-row"><span class="act-lbl">SMS</span><span class="act-val">${s(t.complaintSMSMessage)}</span></div>`);
      return `
        <div class="act-entry">
          <div class="act-header">
            <span class="act-who">${s(t.createdByName)}</span>
            <span class="act-when">${fdt(t.createdAt)}</span>
          </div>
          ${rows.length ? `<div class="act-body">${rows.join('')}</div>` : ''}
        </div>`;
    }).join('') || '<p style="color:#94a3b8;font-size:13px;">No activity records found.</p>';

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>${s(c?.complaintNo)}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600&family=Noto+Nastaliq+Urdu:wght@400;600&display=swap" rel="stylesheet">
  <style>
    *, *::before, *::after { box-sizing: border-box; }
    body {
      margin: 0;
      padding: 28px 32px;
      font-family: 'Roboto', 'Noto Nastaliq Urdu', 'Segoe UI', sans-serif;
      font-size: 13px;
      color: #1e293b;
      background: #fff;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .doc-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 3px solid #04a251;
      padding-bottom: 14px;
      margin-bottom: 20px;
    }
    .doc-system { font-size: 22px; font-weight: 700; color: #04a251; letter-spacing: 0.03em; }
    .doc-sub { font-size: 11px; color: #64748b; margin-top: 2px; }
    .doc-meta { text-align: right; }
    .doc-complaintno { font-size: 16px; font-weight: 700; color: #0f172a; }
    .doc-status {
      display: inline-block;
      margin-top: 6px;
      padding: 3px 12px;
      border-radius: 20px;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      background: #dcfce7;
      color: #166534;
      border: 1px solid #bbf7d0;
    }
    .stats-bar {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 0;
      border: 1px solid #e2e8f0;
      border-top: none;
      border-bottom: none;
      overflow: hidden;
    }
    .stat {
      padding: 12px 16px;
      border-right: 1px solid #e2e8f0;
      border-bottom: 1px solid #e2e8f0;
      background: #f8fafc;
    }
    .stat:last-child { border-right: none; }
    .stat-lbl { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: #94a3b8; margin-bottom: 4px; }
    .stat-val { font-size: 13px; font-weight: 400; color: #0f172a; }
    .section-title {
      background: #04a251;
      color: #fff;
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      padding: 7px 14px;
      border-radius: 6px 6px 0 0;
      margin-bottom: 0;
    }
    .ul { font-family: 'Noto Nastaliq Urdu', serif; font-weight: 400; font-size: 13px; letter-spacing: 0; text-transform: none; }
    table {
      width: 100%;
      border-collapse: collapse;
      border: 1px solid #e2e8f0;
      border-top: none;
      margin-bottom: 18px;
    }
    tr { border-bottom: 1px solid #f1f5f9; }
    tr:last-child { border-bottom: none; }
    td { padding: 9px 14px; vertical-align: top; }
    .lbl {
      width: 38%;
      font-size: 11px;
      font-weight: 600;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      background: #f8fafc;
      border-right: 1px solid #f1f5f9;
    }
    .val { font-size: 13px; font-family: 'Roboto', 'Noto Nastaliq Urdu', 'Segoe UI', sans-serif; color: #0f172a; line-height: 1.6; }
    .desc-box {
      border: 1px solid #e2e8f0;
      border-top: none;
      padding: 16px 18px;
      background: #f8fafc;
      font-size: 13px;
      line-height: 1.8;
      color: #334155;
      margin-bottom: 18px;
      min-height: 60px;
    }
    .desc-box .ur { font-family: 'Noto Nastaliq Urdu', serif; font-size: 15px; direction: rtl; display: block; }
    .act-entry {
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      overflow: hidden;
      margin-bottom: 12px;
      page-break-inside: avoid;
    }
    .act-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 9px 14px;
      background: #f1f5f9;
      border-bottom: 1px solid #e2e8f0;
    }
    .act-who { font-size: 13px; font-weight: 600; color: #0f172a; }
    .act-when { font-size: 11px; color: #94a3b8; }
    .act-body { background: #fff; }
    .act-row { display: flex; gap: 14px; padding: 8px 14px; border-bottom: 1px solid #f1f5f9; }
    .act-row:last-child { border-bottom: none; }
    .act-lbl { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: #94a3b8; min-width: 110px; padding-top: 2px; flex-shrink: 0; }
    .act-val { font-size: 13px; font-family: 'Roboto', 'Noto Nastaliq Urdu', 'Segoe UI', sans-serif; color: #334155; line-height: 1.7; }
    .act-val p { margin: 0; display: inline; }
    .act-val strong, .act-val b { font-weight: normal; }
    .act-val * { font-size: inherit; }
    @media print {
      body { padding: 16px 20px; }
      .stats-bar { break-inside: avoid; }
      .act-entry { break-inside: avoid; }
    }
  </style>
</head>
<body>
  <div class="doc-header">
    <div>
      <div class="doc-system">GRMIS</div>
      <div class="doc-sub">Grievance Redressal Management Information System</div>
    </div>
    <div class="doc-meta">
      <div class="doc-complaintno">${s(c?.complaintNo)}</div>
      <div class="doc-status">${s(c?.currentProcess?.name)}</div>
    </div>
  </div>

  ${citizenSection}

  <div class="section-title">Complaint Details <span class="ul">/ شکایت کی تفصیلات</span></div>
  <div class="stats-bar">
    <div class="stat"><div class="stat-lbl">Segment</div><div class="stat-val">${segment}</div></div>
    <div class="stat"><div class="stat-lbl">Priority</div><div class="stat-val">${s(c?.priority)}</div></div>
    <div class="stat"><div class="stat-lbl">Severity</div><div class="stat-val">${s(c?.severity)}</div></div>
  </div>
  <table>
    ${field('Complainant Type', s(c?.complaintCategoryName), 'شکایت کنندہ کی قسم')}
    ${field('Complaint Type', s(c?.complaintSubcategoryName), 'شکایت کی قسم')}
    ${field('Legal Framework', s(c?.legalFramework), 'قانونی فریم ورک')}
    ${field('Days Required', s(c?.daysRequired), 'دن درکار ہیں')}
    ${field('Expected Resolution Date', fd(c?.expectedResolutionDate), 'متوقع حل کی تاریخ')}
    ${field('Division', s(c?.divisionName), 'ڈویژن')}
    ${field('District', s(c?.districtName), 'ضلع')}
    ${field('Tehsil', s(c?.tehsilName), 'تحصیل')}
    ${field('Mouza', s(c?.mouzaName), 'موضع')}
    ${field('Complaint Address', s(c?.complaintAddress), 'شکایت کا پتہ')}
    ${field('Other Forum', s(c?.otherForum), 'دیگر فورم')}
    ${field('Assigned To', s(c?.assignToName), 'تفویض کردہ')}
    ${field('Assigned Group', s(c?.assignToGroupName), 'تفویض کردہ گروپ')}
  </table>

  <div class="section-title">Description <span class="ul">/ تفصیل</span></div>
  <div class="desc-box"><span class="ur">${s(c?.complaintDetails)}</span></div>

  <div class="section-title">Additional Details <span class="ul">/ اضافی تفصیلات</span></div>
  <table>
    ${field('Work Code', s(c?.workCode), 'ورک کوڈ')}
    ${field('Helpline Identifier', s(c?.helplineIdentifier), 'ہیلپ لائن')}
    ${field('Source', s(c?.complaintSource), 'ذریعہ')}
  </table>

  <div class="section-title">Activity <span class="ul">/ سرگرمی</span></div>
  <div style="padding-top:12px;">${activityRows}</div>

</body>
</html>`;

    const complaintNo = s(c?.complaintNo);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const blobUrl = URL.createObjectURL(blob);
    const printWindow = window.open(blobUrl, '_blank');

    if (!printWindow) {
      alert('Please allow pop-ups to generate the PDF.');
      URL.revokeObjectURL(blobUrl);
      return;
    }

    printWindow.addEventListener('load', () => {
      printWindow.document.title = complaintNo;
      printWindow.document.fonts.ready.then(() => {
        setTimeout(() => {
          printWindow.print();
          URL.revokeObjectURL(blobUrl);
        }, 200);
      });
    });
  }

  private formatDate(date: string | Date | null | undefined, format: string = 'yyyy-MM-dd'): string {
    if (!date) return '';
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return '';
      if (format === 'dd MMM yyyy hh:mm a') {
        const day = d.getDate().toString().padStart(2, '0');
        const month = d.toLocaleString('en', { month: 'short' });
        const year = d.getFullYear();
        let hours = d.getHours();
        const minutes = d.getMinutes().toString().padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;
        return `${day} ${month} ${year} ${hours}:${minutes} ${ampm}`;
      }
      if (format === 'dd MMM yyyy') {
        const day = d.getDate().toString().padStart(2, '0');
        const month = d.toLocaleString('en', { month: 'short' });
        return `${day} ${month} ${d.getFullYear()}`;
      }
      return d.toISOString().split('T')[0];
    } catch {
      return '';
    }
  }
}
